<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue'

defineProps<{
  videoUrl: string
}>()

const model = defineModel()

const circumference = 2 * Math.PI * 20
const progress = ref(0)
const duration = ref(0)
const timeRemaining = ref(0)
const isPlaying = ref(false)
const isLoaded = ref(false)

const dashOffset = computed(() => {
  return circumference * (1 - progress.value / 100)
})

const formattedTime = computed(() => {
  const minutes = Math.floor(timeRemaining.value / 60)
  const seconds = Math.floor(timeRemaining.value % 60)
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
})

function handleMessage(event: MessageEvent) {
  if (event.data.property === 'duration') {
    duration.value = event.data.value
    timeRemaining.value = duration.value
  }
  if (event.data.property === 'currentTime') {
    progress.value = (event.data.value / duration.value) * 100
    timeRemaining.value = duration.value - event.data.value
  }
  if (event.data.property === 'ended' && event.data.value === true) {
    model.value = false
    isPlaying.value = false
  }
  if (event.data.property === 'paused')
    isPlaying.value = !event.data.value
  if (event.data.__privateUnstableMessageType === 'iframeReady')
    isLoaded.value = true
  if (event.data.property === 'play') {
    (window as any).cloudflareVideoController.hideControls()
    isPlaying.value = event.data.value
  }
}

function isMuted() {
  return (window as any).cloudflareVideoController?.muted
}

function unmute() {
  (window as any).cloudflareVideoController.unmute()
}

class CloudflareVideoController {
  iframe: HTMLIFrameElement | null
  player: any

  constructor() {
    this.iframe = null
    this.player = null
    this.initialize()
  }

  initialize() {
    // Find the Cloudflare Stream iframe
    this.iframe = document.querySelector('iframe[src*="cloudflarestream.com"]')

    if (!this.iframe) {
      console.error('Cloudflare Stream iframe not found')
      return
    }
    // Wait for the Cloudflare Stream SDK to be available
    const checkSDK = () => {
      if ((window as any).Stream)
        this.setupPlayer()
      else
        setTimeout(checkSDK, 100)
    }
    checkSDK()
  }

  setupPlayer() {
    try {
      // Create player using Cloudflare's Stream SDK
      this.player = (window as any).Stream(this.iframe)
      this.showControls()

      // Add event listeners
      this.player.addEventListener('play', () => {
        this.postMessage('play', true)
      })

      this.player.addEventListener('pause', () => {
        this.postMessage('paused', true)
      })
    }
    catch (error) {
      console.error('Error setting up Cloudflare Stream player:', error)
    }
  }

  // Helper method to post messages to the parent window
  postMessage(property: string, value: any) {
    window.parent.postMessage({ property, value }, '*')
  }

  play() {
    this.iframe?.focus()
    if (this.player) {
      this.player.play()
        .catch((_error: any) => {
          this.postMessage('muted', true)
          this.player.play()
        })
    }
  }

  pause() {
    if (this.player)
      this.player.pause()
  }

  hideControls() {
    if (this.player)
      this.player.controls = false
  }

  showControls() {
    if (this.player)
      this.player.controls = true
  }

  togglePlay() {
    if (this.player) {
      if (this.player.paused)
        this.play()
      else this.pause()
    }
  }
}

onMounted(() => {
  window.addEventListener('message', handleMessage)
  if (!(window as any).Stream) {
    const script = document.createElement('script')
    script.src = `https://embed.cloudflarestream.com/embed/sdk.latest.js`
    script.async = true
    script.defer = true

    script.onload = () => {
      // Initialize the video controller once the script is loaded
      (window as any).cloudflareVideoController = new CloudflareVideoController()
    }

    document.head.appendChild(script)
  }
  else {
    // If Stream is already available, initialize immediately
    (window as any).cloudflareVideoController = new CloudflareVideoController()
  }
})

onUnmounted(() => {
  window.removeEventListener('message', handleMessage)
})
</script>

<template>
  <div
    class="relative" style="padding-top: 56.25%;" :style="{
      width: '100%',
      height: '100%',
    }"
  >
    <iframe
      id="video-frame"
      :src="videoUrl"
      class="video-frame absolute top-0 left-0 w-full h-full"
      style="border-radius: 0px;"
      allow="accelerometer;gyroscope;encrypted-media;fullscreen"
      allowfullscreen
      loading="lazy"
    />

    <div
      v-if="isLoaded && !isPlaying" class="absolute bottom-0 left-0 w-full h-30"
      style="background: linear-gradient(to top, black, rgba(0, 0, 0, 1));"
    />
    <!-- Progress Circle -->
    <div class="absolute top-10px left-10px w-12 h-12">
      <svg class="w-full h-full" style="transform: rotate(-90deg)">
        <circle
          class="text-gray-600/40"
          stroke-width="4"
          stroke="currentColor"
          fill="transparent"
          r="20"
          cx="24"
          cy="24"
        />
        <circle
          class="text-primary"
          stroke-width="4"
          stroke="currentColor"
          fill="transparent"
          r="20"
          cx="24"
          cy="24"
          :stroke-dasharray="circumference"
          :stroke-dashoffset="dashOffset"
          stroke-linecap="round"
        />
      </svg>
      <div
        class="absolute inset-0 flex items-center justify-center text-white font-bold text-xs"
        style="transform: rotate(0deg)"
      >
        {{ formattedTime }}
      </div>
    </div>
    <!-- Volume Button (Shown only when muted and playing) -->
    <button
      v-if="isPlaying && isMuted()"
      class="absolute bottom-150px right-10px bg-black/50 rounded-full p-2 hover:bg-black/70 transition-all focus:outline-none"
      @click="() => unmute()"
    >
      <svg class="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 24 24">
        <path d="M16.5 12c0-1.77-1.02-3.29-2.5-4.03v2.21l2.45 2.45c.03-.2.05-.41.05-.63zm2.5 0c0 .94-.2 1.82-.54 2.64l1.51 1.51C20.63 14.91 21 13.5 21 12c0-4.28-2.99-7.86-7-8.77v2.06c2.89.86 5 3.54 5 6.71zM4.27 3L3 4.27 7.73 9H3v6h4l5 5v-6.73l4.25 4.25c-.67.52-1.42.93-2.25 1.18v2.06c1.38-.31 2.63-.95 3.69-1.81L19.73 21 21 19.73l-9-9L4.27 3zM12 4L9.91 6.09 12 8.18V4z" />
      </svg>
    </button>
  </div>
</template>

<style>
</style>
